import Box from '@amzn/meridian/box';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Profile } from '../../../Types';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import DataState from '../../../Types/DataState';
import SearchField from '@amzn/meridian/search-field';
import { TrainingCatalogResult } from '../../../api/Training/schema';
import Catalog from './Catalog';
import { getLocale } from '../../../config/Config';
import _ from 'lodash';
import { FilterSection } from './FilterSelection';
import noDataPic from '../../../Assets/NoData.svg';
import { getTrainingCatalog } from '../../../api/Training';
import { useParams } from 'react-router';

interface TrainingCatalogProps {
  profile: Profile;
}

const TrainingCatalog: React.FC<TrainingCatalogProps> = ({ profile }) => {
  const intl = useIntl();

  // State management
  const [trainingCatalog, setTrainingCatalog] =
    useState<TrainingCatalogResult>();
  const [trainingCatalogState, setTrainingCatalogState] = useState(
    DataState.LOADING
  );

  // Helper functions
  const getDefaultLanguage = () => {
    const currentLocale = getLocale() ?? navigator.language;
    const [language, country] = currentLocale.split('-');
    return `${language.toUpperCase()}_${country.toUpperCase()}`;
  };

  const resetFilter = useCallback(() => {
    setFilters({
      query: '',
      language: getDefaultLanguage(),
      metric: 'ALL',
      duration: 'ALL',
    });
  }, []);

  const [filters, setFilters] = useState({
    query: '',
    language: getDefaultLanguage(),
    metric: 'ALL',
    duration: 'ALL',
  });

  const { userId } = useParams<{ userId?: string }>();
  const assignees = useMemo(() => {
    const selectedUser = profile.directReports.find(
      report => report.userID === userId
    );

    if (selectedUser) return [selectedUser];

    return profile.directReports;
  }, [profile]);

  // Data fetching
  useEffect(() => {
    getTrainingCatalog({}, setTrainingCatalog, setTrainingCatalogState);
    resetFilter();
  }, [profile]);

  const { languageSelections, metricSelections, durationSelections } =
    useMemo(() => {
      if (trainingCatalogState !== DataState.LOADED || !trainingCatalog) {
        return {
          languageSelections: [getDefaultLanguage()],
          metricSelections: ['ALL'],
          durationSelections: ['ALL'],
        };
      }

      const catalogs = trainingCatalog.trainings;

      const languages = new Set(
        catalogs
          .flatMap(item => item.trainingLanguage ?? [])
          .filter((lang): lang is string => lang !== undefined)
      );

      const metrics = new Set(
        ['ALL'].concat(
          catalogs
            .flatMap(item => item.metrics ?? [])
            .filter((metric): metric is string => metric !== undefined)
        )
      );

      const durations = new Set(
        ['ALL'].concat(
          catalogs
            .map(item => item.duration)
            .filter((duration): duration is string => duration !== undefined)
        )
      );

      return {
        languageSelections: Array.from(languages),
        metricSelections: Array.from(metrics),
        durationSelections: Array.from(durations),
      };
    }, [trainingCatalog, trainingCatalogState]);

  // Memoized filtered catalogs
  const filteredCatalogs = useMemo(() => {
    if (trainingCatalogState !== DataState.LOADED || !trainingCatalog)
      return [];

    return trainingCatalog.trainings.filter(catalog => {
      const matchesQuery =
        !filters.query ||
        catalog.multilingualAttributes?.some(
          attr =>
            attr.languageId === catalog.trainingLanguage &&
            attr.isDefaultLanguage &&
            attr.trainingTitle
              .toLowerCase()
              .includes(filters.query.toLowerCase())
        );

      const matchesLanguage = catalog.trainingLanguage === filters.language;

      const matchesMetric =
        filters.metric === 'ALL' || catalog.metrics?.includes(filters.metric);

      const matchesDuration =
        filters.duration === 'ALL' ||
        catalog.duration?.includes(filters.duration);

      return (
        matchesQuery && matchesLanguage && matchesMetric && matchesDuration
      );
    });
  }, [trainingCatalog, trainingCatalogState, filters]);

  // Render functions
  const renderLoadingState = () => (
    <Box spacingInset='400 none none none'>
      <Row alignmentHorizontal='center'>
        <Loader type='circular' size='large' />
      </Row>
    </Box>
  );

  const renderErrorState = () => (
    <Box spacingInset='400 none none none'>
      <Alert type='error'>
        <FormattedMessage
          id='cs_perfportal_error_refresh_page'
          defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
        />
      </Alert>
    </Box>
  );

  const renderCatalogList = () => {
    if (filteredCatalogs.length === 0) {
      return (
        <Box>
          <Row
            spacing='none'
            spacingInset='600 none none'
            alignmentHorizontal='center'
          >
            <img src={noDataPic} alt='No data' />
          </Row>

          <Text alignment='center'>
            <FormattedMessage
              id='cs_perfportal_training_experience_no_training_found'
              defaultMessage='No training found'
            />
          </Text>
          <Text alignment='center'>
            <FormattedMessage
              id='cs_perfportal_training_experience_update_selection'
              defaultMessage='Update your selection and try again'
            />
          </Text>
        </Box>
      );
    }

    return (
      <Column spacingInset='400 none none' overflowY='auto' maxHeight='1000px'>
        {filteredCatalogs.map(training => (
          <Catalog
            key={training.learnTrainingId}
            catalog={training}
            assignees={assignees}
          />
        ))}
      </Column>
    );
  };

  const renderContent = () => {
    if (trainingCatalogState === DataState.LOADING) return renderLoadingState();
    if (trainingCatalogState === DataState.ERROR) return renderErrorState();
    return renderCatalogList();
  };

  return (
    <>
      <Row spacingInset='400 none none'>
        <Text type='h500'>
          <FormattedMessage
            id='cs_perfportal_training_catalog_search_trainings'
            defaultMessage='Search trainings'
          />
        </Text>
      </Row>

      <Row
        width='100%'
        widths={['70%']}
        spacing='none 600 none'
        alignmentVertical='top'
      >
        <Box minHeight={70} spacingInset='100'>
          <SearchField
            value={filters.query}
            onChange={query => setFilters(prev => ({ ...prev, query }))}
            placeholder={intl.formatMessage({
              id: 'cs_perfportal_training_catalog_search_for',
              defaultMessage: 'Search for...',
            })}
            onSubmit={() => {}}
            size='xlarge'
            searchButton={true}
            disabled={trainingCatalogState !== DataState.LOADED}
          />
          {renderContent()}
        </Box>

        <FilterSection
          filters={filters}
          setFilters={setFilters}
          selections={{
            language: languageSelections,
            metric: metricSelections,
            duration: durationSelections,
          }}
          onReset={resetFilter}
        />
      </Row>
    </>
  );
};

export default memo(TrainingCatalog);
